// Portal Gestão · Regras de Compliance (V4.53)
// - Lista, cria, edita, ativa/desativa e deleta regras (não-builtin) em db.compliance_rules
// - Requer permissoes.configurar para editar; permissoes.visualizar para ler
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Power, RefreshCw, Loader2, ShieldCheck, Lock, Bell, Mail, Save, X } from 'lucide-react';

const ROLE_OPTIONS = ['portaria', 'coordenacao', 'secretaria', 'direcao_pedagogica', 'professor', 'admin'];
const ACTION_OPTIONS = ['view_sensitive', 'export', 'edit', 'delete', 'print', 'navigate'];
const VERB_OPTIONS = ['cpf', 'endereco', 'telefone', 'saude', 'notas', 'financeiro', 'exportar'];
const MODULE_PERM_OPTIONS = [
  'financeiro.visualizar', 'central_notas.visualizar', 'dados_sensiveis.exportar',
  'dnago.visualizar', 'busca_alunos.visualizar', 'comunicacao.visualizar',
];

const emptyForm = {
  name: '', description: '', enabled: true, priority: 50,
  when: { role_in: [], action_in: [], verb_in: [], require_module_perm: '' },
  alert_reason: '', notify: { email: true, push: false },
};

function MultiChip({ label, options, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {options.map((opt) => {
          const active = (value || []).includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(active ? value.filter((v) => v !== opt) : [...(value || []), opt])}
              className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${active ? 'bg-purple-600 border-purple-700 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

export default function PortalGestaoRegras() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/portal-gestao/rules');
      setRules(data?.items || []);
    } catch (e) {
      toast.error('Falha ao carregar regras');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const toggleRule = async (rule) => {
    try {
      await api.post(`/portal-gestao/rules/${rule.id}/toggle`);
      toast.success(rule.enabled ? 'Regra desabilitada' : 'Regra habilitada');
      fetchRules();
    } catch (e) { toast.error('Falha ao alternar regra'); }
  };

  const deleteRule = async (rule) => {
    if (rule.builtin) return toast.error('Regras builtin não podem ser removidas — desabilite-a.');
    if (!window.confirm(`Excluir regra "${rule.name}"?`)) return;
    try {
      await api.delete(`/portal-gestao/rules/${rule.id}`);
      toast.success('Regra excluída');
      fetchRules();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao excluir');
    }
  };

  const saveNew = async () => {
    if (!form.name.trim()) return toast.error('Nome é obrigatório');
    setSaving(true);
    try {
      const payload = { ...form,
        when: {
          ...(form.when.role_in?.length ? { role_in: form.when.role_in } : {}),
          ...(form.when.action_in?.length ? { action_in: form.when.action_in } : {}),
          ...(form.when.verb_in?.length ? { verb_in: form.when.verb_in } : {}),
          ...(form.when.require_module_perm ? { require_module_perm: form.when.require_module_perm } : {}),
        },
      };
      await api.post('/portal-gestao/rules', payload);
      toast.success('Regra criada');
      setShowForm(false); setForm(emptyForm); fetchRules();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao criar regra');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="portal-gestao-regras">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-purple-700 font-semibold mb-1">Compliance</div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-purple-700" /> Regras de Compliance
          </h1>
          <p className="text-sm text-slate-500 mt-1">Defina quando um acesso deve gerar alerta em tempo real. Ordem de avaliação por <b>prioridade</b> crescente.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={fetchRules} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold" data-testid="regras-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar
          </button>
          <button type="button" onClick={() => { setForm(emptyForm); setShowForm(true); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-700 hover:bg-purple-800 text-white text-sm font-semibold" data-testid="regras-nova">
            <Plus className="w-4 h-4" /> Nova regra
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-purple-200 p-5 mb-6 shadow-sm" data-testid="regras-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-800">Nova regra</h3>
            <button type="button" onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-slate-100" data-testid="regras-form-close"><X className="w-4 h-4 text-slate-500" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nome *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="regras-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Prioridade</label>
              <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="regras-priority" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Descrição</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="regras-desc" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mensagem de alerta</label>
              <input value={form.alert_reason} onChange={(e) => setForm({ ...form, alert_reason: e.target.value })}
                placeholder="Ex: {role} acessou {verb}"
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" data-testid="regras-reason" />
              <div className="text-[10px] text-slate-500 mt-1">Placeholders: <code>{"{verb}"}</code>, <code>{"{action}"}</code>, <code>{"{role}"}</code></div>
            </div>

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
              <MultiChip label="Se role ∈" options={ROLE_OPTIONS} value={form.when.role_in}
                onChange={(v) => setForm({ ...form, when: { ...form.when, role_in: v } })} testid="regras-role-in" />
              <MultiChip label="Se ação ∈" options={ACTION_OPTIONS} value={form.when.action_in}
                onChange={(v) => setForm({ ...form, when: { ...form.when, action_in: v } })} testid="regras-action-in" />
              <MultiChip label="Se verbo ∈" options={VERB_OPTIONS} value={form.when.verb_in}
                onChange={(v) => setForm({ ...form, when: { ...form.when, verb_in: v } })} testid="regras-verb-in" />
            </div>

            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Requer perm de módulo (dispara se usuário NÃO tem)</label>
              <select value={form.when.require_module_perm || ''} onChange={(e) => setForm({ ...form, when: { ...form.when, require_module_perm: e.target.value } })}
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="regras-module-perm">
                <option value="">— nenhum —</option>
                {MODULE_PERM_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="md:col-span-2 flex flex-wrap items-center gap-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
              <label className="text-sm text-slate-700 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.notify.email} onChange={(e) => setForm({ ...form, notify: { ...form.notify, email: e.target.checked } })} data-testid="regras-notify-email" />
                <Mail className="w-4 h-4 text-slate-500" /> Enviar email
              </label>
              <label className="text-sm text-slate-700 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.notify.push} onChange={(e) => setForm({ ...form, notify: { ...form.notify, push: e.target.checked } })} data-testid="regras-notify-push" />
                <Bell className="w-4 h-4 text-slate-500" /> Push FCM
              </label>
              <label className="text-sm text-slate-700 flex items-center gap-2 cursor-pointer ml-auto">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="regras-enabled" />
                <Power className="w-4 h-4 text-emerald-600" /> Ativa ao criar
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold" data-testid="regras-cancel">Cancelar</button>
            <button type="button" onClick={saveNew} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-700 hover:bg-purple-800 text-white text-sm font-semibold disabled:opacity-50" data-testid="regras-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar regra
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="regras-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 w-12">P.</th>
              <th className="text-left px-4 py-2">Regra</th>
              <th className="text-left px-4 py-2">Quando</th>
              <th className="text-left px-4 py-2">Motivo</th>
              <th className="text-center px-4 py-2">Notif</th>
              <th className="text-center px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nenhuma regra configurada.</td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`regras-row-${r.id.slice(0, 8)}`}>
                <td className="px-4 py-3 text-slate-500 font-mono">{r.priority}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">{r.name}</span>
                    {r.builtin && <span className="text-[9px] uppercase tracking-wider bg-slate-100 border border-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-semibold flex items-center gap-1"><Lock className="w-2.5 h-2.5" />builtin</span>}
                  </div>
                  {r.description && <div className="text-[11px] text-slate-500 mt-0.5">{r.description}</div>}
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-600">
                  {r.when?.role_in?.length ? <div>role ∈ [{r.when.role_in.join(', ')}]</div> : null}
                  {r.when?.action_in?.length ? <div>action ∈ [{r.when.action_in.join(', ')}]</div> : null}
                  {r.when?.verb_in?.length ? <div>verb ∈ [{r.when.verb_in.join(', ')}]</div> : null}
                  {r.when?.require_module_perm ? <div>sem <code>{r.when.require_module_perm}</code></div> : null}
                </td>
                <td className="px-4 py-3 text-[12px] text-rose-700 font-mono">{r.alert_reason || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-[10px]">
                    {r.notify?.email && <span title="email" className="p-1 rounded bg-blue-50 text-blue-700"><Mail className="w-3 h-3" /></span>}
                    {r.notify?.push && <span title="push" className="p-1 rounded bg-purple-50 text-purple-700"><Bell className="w-3 h-3" /></span>}
                    {!(r.notify?.email || r.notify?.push) && <span className="text-slate-400">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  {r.enabled ? (
                    <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">ativa</span>
                  ) : (
                    <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">off</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button type="button" onClick={() => toggleRule(r)}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title={r.enabled ? 'Desabilitar' : 'Habilitar'}
                      data-testid={`regras-toggle-${r.id.slice(0, 8)}`}>
                      <Power className={`w-4 h-4 ${r.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                    </button>
                    <button type="button" onClick={() => deleteRule(r)} disabled={r.builtin}
                      className={`p-1.5 rounded ${r.builtin ? 'opacity-30 cursor-not-allowed' : 'hover:bg-rose-50 text-rose-600'}`}
                      title={r.builtin ? 'Builtin — desabilite em vez de deletar' : 'Excluir'}
                      data-testid={`regras-delete-${r.id.slice(0, 8)}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
